import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/theme-toggle";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useCompanyLogo } from "@/hooks/use-company-logo";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { ShoppingCart, History, Package, LogOut, MessageSquare, Bell } from "lucide-react";
import NotificationBell from "@/components/notification-bell";
import { usePushSubscription } from "@/hooks/use-push-subscription";
import NotificationPermissionBanner from "@/components/notification-permission-banner";

const menuItems = [
  { title: "Beställ", href: "/", icon: ShoppingCart },
  { title: "Historik", href: "/history", icon: History, forceReload: true },
  { title: "Support", href: "/support", icon: MessageSquare },
  { title: "Notifikationer", href: "/notifications", icon: Bell },
];

interface ClientSession {
  id: string;
  name: string;
  email?: string;
}

export default function ClientLayout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const logoUrl = useCompanyLogo();
  
  const { data: client } = useQuery<ClientSession | null>({
    queryKey: ["/api/client-auth/me"],
  });

  usePushSubscription();

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/client-auth/logout");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-auth/me"] });
      setLocation("/");
    },
  });

  const sidebarStyle = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <Sidebar>
          <SidebarHeader className="p-4">
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="Rätt Order" className="h-8" />
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Meny</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {menuItems.map((item) => (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton
                        asChild
                        isActive={location === item.href}
                      >
                        {item.forceReload ? (
                          <a href={item.href} onClick={(e) => { e.preventDefault(); window.location.href = item.href; }}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </a>
                        ) : (
                          <Link href={item.href}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter className="p-4">
            {client && (
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    {client.name?.[0] || "K"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {client.name}
                  </p>
                  {client.email && (
                    <p className="text-xs text-muted-foreground truncate">
                      {client.email}
                    </p>
                  )}
                </div>
              </div>
            )}
          </SidebarFooter>
        </Sidebar>

        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between gap-4 p-3 border-b bg-background">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <div className="flex items-center gap-2">
              <NotificationBell />
              <ThemeToggle />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
                data-testid="button-logout"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </header>
          <main className="flex-1 overflow-auto p-3 sm:p-6">
            <NotificationPermissionBanner subscribeEndpoint="/api/client/push/subscribe" />
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
